import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { agentAPI, retailersAPI } from '../services/api';
import './Dashboard.css';

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchRetailers();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await agentAPI.getDashboard();
      setDashboard(data?.data || data);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchRetailers = async () => {
    try {
      const data = await retailersAPI.getRetailers();
      setRetailers(data?.data || data || []);
    } catch (err) {
      // Optionally show error for retailers
      setRetailers([]);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loader"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="alert alert-error">{error}</div>
        <button className="button button-primary" onClick={fetchDashboard}>
          Retry
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-empty">No data available</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      
      <div className="dashboard-header">
        <div>
          <h1>Welcome back, {user?.name || 'Agent'}</h1>
          <p>Your loan distribution performance dashboard</p>
        </div>
        <button className="button button-primary" onClick={fetchDashboard}>
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-4">
        <div className="dashboard-card">
          <div className="card-icon">📋</div>
          <div className="card-content">
            <div className="card-label">Total Loans</div>
            <div className="card-value">{dashboard?.totalLoans || 0}</div>
            <div className="card-subtitle">Submitted</div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <div className="card-label">Approved</div>
            <div className="card-value">{dashboard?.approvedLoans || 0}</div>
            <div className="card-subtitle">Active loans</div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">Collected</div>
            <div className="card-value">₹{(dashboard?.totalCollected || 0).toLocaleString()}</div>
            <div className="card-subtitle">This month</div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <div className="card-label">Success Rate</div>
            <div className="card-value">{dashboard?.successRate || 0}%</div>
            <div className="card-subtitle">Approval rate</div>
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="dashboard-section">
        <h2>Financial Overview</h2>
        <div className="grid grid-2">
          <div className="dashboard-card">
            <div className="card-icon">💵</div>
            <div className="card-content">
              <div className="card-label">Total Distributed</div>
              <div className="card-value">₹{(dashboard?.totalDistributed || 0).toLocaleString()}</div>
              <div className="card-subtitle">All time</div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <div className="card-label">Average Loan</div>
              <div className="card-value">₹{(dashboard?.averageLoan || 0).toLocaleString()}</div>
              <div className="card-subtitle">Per application</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {dashboard?.recentLoans && dashboard.recentLoans.length > 0 && (
        <div className="dashboard-section">
          <h2>Recent Loans</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Retailer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.recentLoans.map((loan) => (
                <tr key={loan.id}>
                  <td>{loan.retailerName}</td>
                  <td>₹{loan.amount.toLocaleString()}</td>
                  <td>
                    <span className={`badge badge-${loan.status.toLowerCase()}`}>
                      {loan.status}
                    </span>
                  </td>
                  <td>{new Date(loan.date).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Onboarded Retailers */}
      <div className="dashboard-section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>My Retailers ({retailers.length})</h2>
          <button 
            className="button button-primary" 
            onClick={() => navigate('/register-retailer')}
          >
            Add New Retailer
          </button>
        </div>
        
        {retailers.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Shop Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {retailers.slice(0, 5).map((retailer) => (
                <tr key={retailer.id}>
                  <td>{retailer.name}</td>
                  <td>{retailer.shop_name}</td>
                  <td>{retailer.phone}</td>
                  <td>{retailer.email || 'N/A'}</td>
                  <td>{new Date(retailer.created_at || retailer.registered_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-data">
            <p>No retailers registered yet.</p>
            <button 
              className="button button-primary" 
              onClick={() => navigate('/register-retailer')}
            >
              Register Your First Retailer
            </button>
          </div>
        )}
        
        {retailers.length > 5 && (
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button 
              className="button button-secondary" 
              onClick={() => navigate('/retailers')}
            >
              View All {retailers.length} Retailers
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <button 
            className="button button-primary"
            onClick={() => navigate('/register-retailer')}
          >
            Register New Retailer
          </button>
          <button 
            className="button button-primary"
            onClick={() => navigate('/submit-loan')}
          >
            Submit Loan
          </button>
          <button 
            className="button button-primary"
            onClick={() => navigate('/record-collection')}
          >
            Record Collection
          </button>
          <button 
            className="button button-secondary"
            onClick={() => navigate('/reports')}
          >
            View Reports
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
